// Copyright (c) 2026. Hardfin, Inc. All rights reserved.

/**
 * Drift resolution popover. Anchored to the entity-title chip, lists
 * per-field Hardfin↔Salesforce discrepancies, and offers Pull or
 * Dismiss per row.
 *
 * Visibility contract (per product spec):
 *  - The popover only closes on user-initiated outside-click / Esc.
 *  - Mutation success and query invalidation must NOT auto-close.
 *  - All-resolved (fields=[]) shows an "All synced" message and stays
 *    open; the user closes it themselves.
 */
import { TooltipDialog } from '@/components/tooltip-dialog';
import {
  KeyboardArrowDownRounded,
  KeyboardArrowRightRounded,
  OpenInNewRounded,
} from '@/constants/hardfin-standard-icons';
import { SFDCDriftEntityType } from '@/sfdc/api/types';
import { DriftFieldCard } from '@/sfdc/components/drift-field-card';
import { useSFDCDriftDiff } from '@/sfdc/hooks/use-sfdc-drift-diff';
import {
  Box,
  Chip,
  Collapse,
  Link as MuiLink,
  PopoverActions,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';

interface Props {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  entityType: SFDCDriftEntityType;
  entityKey: string;
  /** Human-readable entity name surfaced in the pull-success toast. */
  entityDisplayName?: string;
  /**
   * Direct URL to the linked record on the CRM (Salesforce today).
   * When provided, the popover renders a "View on {label}" link at
   * the bottom right that opens in a new tab.
   */
  externalUrl?: string | null;
  /** Display label for the CRM ("Salesforce"); pairs with externalUrl. */
  externalLabel?: string;
  /**
   * Fires after every successful Pull / Dismiss. Hook-level
   * invalidations (diff query + entity-detail query) always run
   * first; this callback is the extension point for callers that
   * need to refresh additional queries — e.g., the location list
   * page invalidates its list query in here.
   */
  onResolved?: () => void;
}

export function DriftResolutionPopover(props: Props) {
  const { fields, dismissed, isLoading } = useSFDCDriftDiff(
    props.entityType,
    props.entityKey,
    { enabled: props.open }
  );
  // Local-only toggle for the "Dismissed records" section. Reset on
  // each open — the popover stays mounted (render-at-root) so state
  // would otherwise persist across opens.
  const [isDismissedOpen, setIsDismissedOpen] = useState(false);
  useEffect(() => {
    if (props.open) {
      setIsDismissedOpen(false);
    }
  }, [props.open]);
  // Drive MUI Popover's imperative `updatePosition` from the
  // Collapse's transition endpoints. The popover anchors at a fixed
  // corner of the trigger; when the body grows it can spill past the
  // viewport. updatePosition re-runs MUI's viewport-clamp logic with
  // the final post-transition height so the popover shifts back into
  // view.
  const popoverActionRef = useRef<PopoverActions>(null);
  const repositionPopover = () => {
    popoverActionRef.current?.updatePosition();
  };

  // Subheader text: count what's still mismatched. Stays accurate as
  // the user resolves rows because the query invalidates after each
  // mutation. When everything's resolved the subtitle drops away —
  // the body's "All fields are in sync with X" placeholder is the
  // single in-sync message; the chip in the header already signals
  // the state.
  const subtitle =
    fields.length === 0
      ? undefined
      : `${fields.length} ${fields.length === 1 ? 'field has' : 'fields have'} mismatched data between systems`;

  // Hold the chip until the diff query resolves — rendering "In sync"
  // during initial load (when fields is still []) would flash green
  // → amber once an actually-drifted entity loads.
  const isInSync = fields.length === 0;
  const statusChip = isLoading ? (
    <Chip size="small" label="Loading…" />
  ) : (
    <Chip
      size="small"
      color={isInSync ? 'success' : 'warning'}
      label={isInSync ? 'In sync' : 'Out of sync'}
    />
  );

  const viewLink = props.externalUrl ? (
    <MuiLink
      href={props.externalUrl}
      target="_blank"
      rel="noreferrer"
      variant="body2"
      underline="hover"
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
    >
      View on {props.externalLabel ?? 'CRM'}
      <OpenInNewRounded fontSize="inherit" />
    </MuiLink>
  ) : null;

  // Header row: View-on-CRM link on the left, status chip on the
  // right. Chip color collapses dismissed rows into "In sync" — the
  // user already acknowledged those divergences, so they don't count
  // toward the "needs review" rollup the chip is meant to mirror.
  const headerAction = (
    <Stack direction="row" alignItems="center" justifyContent="space-between">
      {viewLink ?? <span />}
      {statusChip}
    </Stack>
  );

  return (
    <TooltipDialog
      anchorEl={props.anchorEl}
      open={props.open}
      onClose={props.onClose}
      subtitle={subtitle}
      headerAction={headerAction}
      actionRef={popoverActionRef}
    >
      {isLoading ? (
        <Stack spacing={2}>
          <Skeleton variant="rounded" height={96} />
        </Stack>
      ) : (
        <Stack spacing={2} sx={{ maxHeight: 520, overflowY: 'auto' }}>
          {fields.length === 0 ? (
            <Stack alignItems="center" paddingY={3} spacing={1}>
              <Typography variant="body2" color="text.secondary">
                All fields are in sync with {props.externalLabel ?? 'the CRM'}
              </Typography>
            </Stack>
          ) : (
            fields.map((field) => (
              <DriftFieldCard
                key={field.fieldName}
                entityType={props.entityType}
                entityKey={props.entityKey}
                entityDisplayName={props.entityDisplayName}
                field={field}
                onResolved={props.onResolved}
              />
            ))
          )}
          {dismissed.length > 0 && (
            <Stack spacing={1}>
              <Box
                component="button"
                type="button"
                onClick={() => setIsDismissedOpen((open) => !open)}
                aria-expanded={isDismissedOpen}
                sx={{
                  background: 'transparent',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color: 'text.secondary',
                  font: 'inherit',
                }}
              >
                {isDismissedOpen ? (
                  <KeyboardArrowDownRounded fontSize="small" />
                ) : (
                  <KeyboardArrowRightRounded fontSize="small" />
                )}
                <Typography variant="subtitle2" color="text.secondary">
                  Dismissed records ({dismissed.length})
                </Typography>
              </Box>
              <Collapse
                in={isDismissedOpen}
                unmountOnExit
                onEntered={repositionPopover}
                onExited={repositionPopover}
              >
                <Stack spacing={2}>
                  {dismissed.map((field) => (
                    <DriftFieldCard
                      key={field.fieldName}
                      entityType={props.entityType}
                      entityKey={props.entityKey}
                      entityDisplayName={props.entityDisplayName}
                      field={field}
                      dismissal={{
                        dismissedAt: field.dismissedAt,
                        dismissedBy: field.dismissedBy,
                      }}
                      onResolved={props.onResolved}
                    />
                  ))}
                </Stack>
              </Collapse>
            </Stack>
          )}
        </Stack>
      )}
    </TooltipDialog>
  );
}
